import { supabase } from './supabase';

// Logo constraints
export const LOGO_CONSTRAINTS = {
  maxFileSize: 2 * 1024 * 1024, // 2MB
  maxWidth: 400,
  maxHeight: 400,
  allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  allowedExtensions: ['.jpg', '.jpeg', '.png', '.webp'],
  maintainAspectRatio: true
};

/**
 * Validate image file against constraints
 */
export const validateImageFile = (file) => {
  const errors = [];

  // Check file size
  if (file.size > LOGO_CONSTRAINTS.maxFileSize) {
    errors.push(`File size must be less than ${LOGO_CONSTRAINTS.maxFileSize / (1024 * 1024)}MB`);
  }

  // Check file type
  if (!LOGO_CONSTRAINTS.allowedTypes.includes(file.type)) {
    errors.push('File must be JPG, PNG, or WebP format');
  }

  // Check file extension
  const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
  if (!LOGO_CONSTRAINTS.allowedExtensions.includes(extension)) {
    errors.push('File must have .jpg, .jpeg, .png, or .webp extension');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Resize image to fit within constraints while maintaining aspect ratio
 */
export const resizeImage = (file) => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      const maxWidth = LOGO_CONSTRAINTS.maxWidth;
      const maxHeight = LOGO_CONSTRAINTS.maxHeight;

      // Scale down if necessary
      if (width > maxWidth || height > maxHeight) {
        const aspectRatio = width / height;
        
        if (width > height) {
          width = maxWidth;
          height = width / aspectRatio;
        } else {
          height = maxHeight;
          width = height * aspectRatio;
        }
      }

      // Set canvas dimensions
      canvas.width = width;
      canvas.height = height;

      // Draw resized image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(new File([blob], file.name, { type: file.type }));
          } else {
            reject(new Error('Failed to resize image'));
          }
        },
        file.type,
        0.9 // Quality for JPEG
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Upload logo to Supabase Storage
 */
export const uploadLogo = async (organizationId, file) => {
  try {
    // Check authentication status
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      console.error('Authentication error:', authError);
      throw new Error('User not authenticated');
    }
    console.log('Authenticated user:', user.id, 'uploading for organization:', organizationId);

    // Check user's role in the organization
    const { data: membership, error: membershipError } = await supabase
      .from('organization_members')
      .select('role')
      .eq('user_id', user.id)
      .eq('organization_id', organizationId)
      .single();

    if (membershipError || !membership) {
      console.error('Membership check error:', membershipError);
      throw new Error('User is not a member of this organization');
    }

    console.log('User role in organization:', membership.role);
    if (!['owner', 'admin', 'user'].includes(membership.role)) {
      throw new Error('User does not have permission to upload logos (requires owner, admin, or user role)');
    }

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    // Resize image if needed
    const resizedFile = await resizeImage(file);

    // Generate unique filename
    const fileExtension = resizedFile.name.substring(resizedFile.name.lastIndexOf('.'));
    const fileName = `${organizationId}/logo-${Date.now()}${fileExtension}`;

    console.log('Upload details:', {
      originalFileName: file.name,
      resizedFileName: resizedFile.name,
      finalFileName: fileName,
      fileSize: resizedFile.size,
      fileType: resizedFile.type,
      organizationId
    });

    // Upload to Supabase Storage
    const { data, error } = await supabase.storage
      .from('organization-logos')
      .upload(fileName, resizedFile, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Supabase Storage upload error:', error);
      console.error('Error details:', {
        message: error.message,
        status: error.status,
        statusCode: error.statusCode,
        details: error.details,
        name: error.name,
        stack: error.stack
      });
      console.error('Full error object:', JSON.stringify(error, null, 2));
      throw error;
    }

    // Get public URL
    const { data: urlData } = supabase.storage
      .from('organization-logos')
      .getPublicUrl(fileName);

    return {
      success: true,
      logoUrl: urlData.publicUrl,
      fileName: fileName
    };

  } catch (error) {
    console.error('Logo upload error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Update organization logo URL in database
 */
export const updateOrganizationLogo = async (organizationId, logoUrl) => {
  try {
    console.log('Updating organization logo:', { organizationId, logoUrl });
    
    const { data, error } = await supabase
      .from('organizations')
      .update({ logo_url: logoUrl })
      .eq('id', organizationId)
      .select()
      .single();

    if (error) {
      console.error('Supabase update error:', error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw error;
    }

    console.log('Successfully updated organization logo:', data);
    return {
      success: true,
      organization: data
    };

  } catch (error) {
    console.error('Update organization logo error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Delete logo from storage and update organization
 */
export const deleteLogo = async (organizationId, logoUrl) => {
  try {
    // Extract file path from URL
    if (logoUrl) {
      const urlParts = logoUrl.split('/');
      const fileName = urlParts.slice(-2).join('/'); // Get organizationId/filename

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('organization-logos')
        .remove([fileName]);

      if (storageError) {
        console.warn('Storage deletion warning:', storageError);
      }
    }

    // Update organization to remove logo URL
    const { data, error } = await supabase
      .from('organizations')
      .update({ logo_url: null })
      .eq('id', organizationId)
      .select()
      .single();

    if (error) {
      throw error;
    }

    return {
      success: true,
      organization: data
    };

  } catch (error) {
    console.error('Delete logo error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Complete logo upload process (upload + update organization)
 */
export const uploadAndSetLogo = async (organizationId, file) => {
  try {
    // Upload the file
    const uploadResult = await uploadLogo(organizationId, file);
    if (!uploadResult.success) {
      return uploadResult;
    }

    // Update organization
    const updateResult = await updateOrganizationLogo(organizationId, uploadResult.logoUrl);
    if (!updateResult.success) {
      // If database update fails, try to clean up uploaded file
      try {
        const fileName = uploadResult.fileName;
        await supabase.storage
          .from('organization-logos')
          .remove([fileName]);
      } catch (cleanupError) {
        console.warn('Failed to cleanup uploaded file:', cleanupError);
      }
      return updateResult;
    }

    return {
      success: true,
      organization: updateResult.organization,
      logoUrl: uploadResult.logoUrl
    };

  } catch (error) {
    console.error('Complete logo upload error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Get logo preview URL for display
 */
export const getLogoPreviewUrl = (file) => {
  return URL.createObjectURL(file);
};

/**
 * Clean up preview URL to prevent memory leaks
 */
export const cleanupPreviewUrl = (url) => {
  URL.revokeObjectURL(url);
}; 